import { AppConfig, OE_TEMPLATES } from 'src/api/types'
import { removeTrailingSlash } from 'src/refactor/helpers/is-browser'

const planManagerApi =
  window.APP_CONFIG?.REACT_APP_PLAN_MANAGER_API_URL ||
  process.env.REACT_APP_PLAN_MANAGER_API_URL
const siteEnv = planManagerApi?.split('.').pop()

export interface ParsedConfig {
  // Styles
  template: OE_TEMPLATES
  customStyle?: string
  // Text
  headerText: string
  footerText?: string
  withCentivo: string[]
  networkOfExcellence: string[]
  providerPhone?: string
  // Image
  appLogo?: string
  heroImage?: string
  // Video
  video: {
    enabled: boolean
    url: string
  }
  // Component flags
  shouldShowAlreadyMember: boolean
  shouldShowContactForm: boolean
  shouldShowDisclaimer: boolean
  shouldShowEligibility: boolean
  shouldShowEligibilityLink?: boolean
  shouldShowHeader: boolean
  shouldShowHero: boolean
  shouldShowMainInfo: boolean
  shouldShowNetworkOfExcellence: boolean
  shouldShowNumberOnMenu: boolean
  shouldShowSearch: boolean
  shouldShowTestimonials: boolean
  shouldShowUnderConstruction: boolean
  shouldShowVpc: boolean
  displayPcpActivationBadge: boolean
  displayReferralRequiredBadge: boolean
  centivoCare: AppConfig['CENTIVO_CARE']
  isCentivoCareEnabled: boolean
  // Other
  eligibilityLinkUrl?: string
  eligibilityZipCodes?: string[]
  formatPhone?: boolean
  networkSelectionEnabled: boolean
  heroBtn?: AppConfig['REACT_APP_HERO_BTN']
  externalLink?: AppConfig['REACT_APP_EXTERNAL_LINK']
  description?: string
  pageTitle?: string
  preferredNetworkCodes: string[]
}

const defaultCentivoCare: AppConfig['CENTIVO_CARE'] = {
  title: 'FREE virtual care when you need it, where you want it',
  body: 'Centivo Care is a convenient alternative to in-person primary care — and all visits are entirely FREE! With Centivo Care, you have access to a primary care team with integrated behavioral health, plus access to a clinician 24/7/365 for urgent needs. With our simple, engaging app, you can visit with your doctor from the comfort of your home, at work or on the go — from your phone or computer. If at any point you need specialist attention or to be seen in person, your Centivo Care team will guide you to appropriate in-network virtual or community providers.',
  text: 'Learn more',
  link: 'http://centivocare.com/',
}

const expectedConfigurations = {
  'template-1': {
    shouldShowHero: true,
    shouldShowMainInfo: true,
    shouldShowTestimonials: true,
    shouldShowNetworkOfExcellence: true,
    shouldShowEligibility: false,
    shouldShowSearch: true,
    shouldShowContactForm: false,
    shouldShowVpc: false,
    centivoCare: defaultCentivoCare,
    isCentivoCareEnabled: false,
    shouldShowDisclaimer: true,
    shouldShowAlreadyMember: true,
    formatPhone: true,
    headerText:
      'Searching for an affordable, easy-to-use health plan? You found it.',
    footerText: '',
  },
  'template-2': {
    shouldShowAlreadyMember: true,
    shouldShowHero: true,
    shouldShowMainInfo: false,
    shouldShowTestimonials: false,
    shouldShowNetworkOfExcellence: true,
    shouldShowEligibility: false,
    shouldShowSearch: true,
    shouldShowContactForm: false,
    shouldShowVpc: false,
    centivoCare: defaultCentivoCare,
    isCentivoCareEnabled: false,
    shouldShowDisclaimer: true,
    formatPhone: true,
    headerText:
      'Searching for an affordable, easy-to-use health plan? You found it.',
    footerText: '',
  },
  'template-3': {
    shouldShowAlreadyMember: true,
    shouldShowHero: true,
    shouldShowMainInfo: false,
    shouldShowTestimonials: false,
    shouldShowNetworkOfExcellence: false,
    shouldShowEligibility: false,
    shouldShowSearch: true,
    shouldShowContactForm: false,
    shouldShowVpc: false,
    centivoCare: defaultCentivoCare,
    isCentivoCareEnabled: false,
    shouldShowDisclaimer: true,
    formatPhone: true,
    headerText:
      "Let's get started. Find a doctor or facility in your plan's network.",
    footerText: '',
  },
  'template-eligibility': {
    shouldShowAlreadyMember: false,
    shouldShowHero: false,
    shouldShowMainInfo: false,
    shouldShowTestimonials: false,
    shouldShowNetworkOfExcellence: false,
    shouldShowEligibility: true,
    shouldShowSearch: false,
    shouldShowContactForm: false,
    shouldShowVpc: false,
    isCentivoCareEnabled: false,
    centivoCare: {
      title: '',
      body: '',
      text: '',
      link: '',
    },
    shouldShowDisclaimer: false,
    formatPhone: true,
    headerText: '',
    footerText: '',
  },
  'template-underConstruction': {
    shouldShowAlreadyMember: false,
    shouldShowHero: false,
    shouldShowMainInfo: false,
    shouldShowTestimonials: false,
    shouldShowNetworkOfExcellence: false,
    shouldShowEligibility: false,
    shouldShowSearch: false,
    shouldShowContactForm: false,
    shouldShowVpc: false,
    isCentivoCareEnabled: false,
    centivoCare: {
      title: '',
      body: '',
      text: '',
      link: '',
    },
    shouldShowDisclaimer: false,
    formatPhone: false,
    headerText: '',
    footerText: '',
  },
  'template-4': {
    shouldShowHero: true,
    shouldShowMainInfo: false,
    shouldShowTestimonials: false,
    shouldShowNetworkOfExcellence: false,
    shouldShowEligibility: false,
    shouldShowSearch: true,
    shouldShowContactForm: false,
    shouldShowVpc: false,
    centivoCare: {
      title: '',
      body: '',
      text: '',
      link: '',
    },
    isCentivoCareEnabled: false,
    shouldShowDisclaimer: true,
    shouldShowAlreadyMember: true,
    formatPhone: true,
    headerText:
      "Let's get started. Find a doctor or facility in your plan's network.",
    footerText: '',
  },
}

export const parseConfig = (appConfig: AppConfig): ParsedConfig => {
  const isTrueOrDefault = <D extends boolean | undefined>(
    value: string | boolean | undefined,
    defaultValue: D
  ): D => {
    return (
      value !== undefined && value !== null
        ? value.toString() === 'true'
        : defaultValue
    ) as D
  }

  const customStyleFile =
    window.location.hostname === 'localhost'
      ? `${process.env.REACT_APP_DEBUG_SITE_URL ?? 'local'}.centivo.${siteEnv}`
      : window.location.hostname

  const customStyle = `/styles/${removeTrailingSlash(customStyleFile)}.css`

  const template: ParsedConfig['template'] = Object.keys(
    expectedConfigurations
  ).includes(appConfig.REACT_APP_TEMPLATE)
    ? appConfig.REACT_APP_TEMPLATE
    : 'template-1'

  const heroBtn = appConfig.REACT_APP_HERO_BTN
  const externalLink = appConfig.REACT_APP_EXTERNAL_LINK
  const description = appConfig.REACT_APP_DESCRIPTION

  const shouldShowHero = appConfig.SHOW_HERO
  const shouldShowHeader = appConfig.SHOW_HEADER

  const shouldShowMainInfo = appConfig.SHOW_MAIN_INFO
  const shouldShowTestimonials = appConfig.SHOW_TESTIMONIALS
  const shouldShowContactForm = appConfig.SHOW_CONTACT_FORM

  const shouldShowNetworkOfExcellence = appConfig.SHOW_NETWORK_OF_EXCELLENCE

  const shouldShowEligibility = appConfig.SHOW_ELIGIBILITY

  const showEligibilityLink = appConfig.SHOW_ELIGIBILITY_LINK
  const eligibilityLinkUrl = appConfig.ELIGIBILITY_LINK_URL
  const shouldShowEligibilityLink =
    isTrueOrDefault(showEligibilityLink, false) && !!eligibilityLinkUrl

  const shouldShowSearch = appConfig.SHOW_SEARCH

  const shouldShowVpc = appConfig.SHOW_VPC
  const displayPcpActivationBadge = isTrueOrDefault(
    appConfig.DISPLAY_PCP_ACTIVATION_BADGE,
    true
  )
  const displayReferralRequiredBadge = isTrueOrDefault(
    appConfig.DISPLAY_REFERRAL_REQUIRED_BADGE,
    true
  )

  const centivoCare = appConfig.CENTIVO_CARE

  const shouldShowDisclaimer = appConfig.SHOW_DISCLAIMER

  const networkPlanSelection = appConfig.REACT_APP_PLAN_NETWORKS

  const shouldShowNumberOnMenu = appConfig.SHOW_NUMBER_ON_MENU

  const shouldShowAlreadyMember = appConfig.SHOW_ALREADY_MEMBER

  const providerPhone = appConfig.REACT_APP_PHONE

  const headerText = appConfig.REACT_APP_HEADER_TEXT

  const formatPhone = appConfig.FORMAT_PHONE

  const footerText = appConfig.FOOTER_TEXT

  const eligibilityZipCodes = appConfig.ELIGIBILITY_ZIP_CODES

  const appLogo = appConfig.REACT_APP_LOGO
  const heroImage = appConfig.REACT_APP_HERO_IMAGE

  const withCentivo = appConfig.REACT_APP_WITH_CENTIVO_WILL_GET

  const networkOfExcellence = appConfig.REACT_APP_NETWORK_OF_EXCELLENCE

  const preferredNetworkCodes = appConfig.REACT_APP_PREFERRED_NETWORK_CODES

  const shouldShowUnderConstruction =
    appConfig.REACT_APP_TEMPLATE === 'template-underConstruction' ||
    process.env.REACT_APP_TEMPLATE === 'template-underConstruction'

  const videoUrl = appConfig.VIDEO_URL ?? ''
  const videoEnabled = !videoUrl
    ? false
    : isTrueOrDefault(appConfig.VIDEO_ENABLED, false)

  const pageTitle = appConfig.PAGE_TITLE

  const defaultConfig = expectedConfigurations[template]

  return {
    template: template as ParsedConfig['template'],
    shouldShowAlreadyMember: isTrueOrDefault(
      shouldShowAlreadyMember,
      defaultConfig.shouldShowAlreadyMember
    ),
    shouldShowHeader:
      shouldShowHeader !== undefined ? shouldShowHeader !== 'false' : true,
    shouldShowHero: isTrueOrDefault(
      shouldShowHero,
      defaultConfig.shouldShowHero
    ),
    shouldShowContactForm: isTrueOrDefault(
      shouldShowContactForm,
      defaultConfig.shouldShowContactForm
    ),
    shouldShowMainInfo: isTrueOrDefault(
      shouldShowMainInfo,
      defaultConfig.shouldShowMainInfo
    ),
    shouldShowTestimonials: isTrueOrDefault(
      shouldShowTestimonials,
      defaultConfig.shouldShowTestimonials
    ),
    shouldShowNetworkOfExcellence: isTrueOrDefault(
      shouldShowNetworkOfExcellence,
      defaultConfig.shouldShowNetworkOfExcellence
    ),
    shouldShowEligibility: isTrueOrDefault(
      shouldShowEligibility,
      defaultConfig.shouldShowEligibility
    ),
    shouldShowEligibilityLink,
    eligibilityLinkUrl,
    shouldShowSearch: isTrueOrDefault(
      shouldShowSearch,
      defaultConfig.shouldShowSearch
    ),
    shouldShowVpc: isTrueOrDefault(shouldShowVpc, defaultConfig.shouldShowVpc),
    displayPcpActivationBadge,
    displayReferralRequiredBadge,
    centivoCare: {
      title: centivoCare?.title || defaultCentivoCare.title,
      body: centivoCare?.body || defaultCentivoCare.body,
      link: centivoCare?.link || defaultCentivoCare.link,
      text: centivoCare?.text || defaultCentivoCare.text,
    },
    isCentivoCareEnabled: isTrueOrDefault(
      window?.APP_CONFIG?.CENTIVO_CARE_ENABLED,
      defaultConfig.isCentivoCareEnabled
    ),
    shouldShowDisclaimer: isTrueOrDefault(
      shouldShowDisclaimer,
      defaultConfig.shouldShowDisclaimer
    ),
    networkSelectionEnabled: networkPlanSelection
      ? networkPlanSelection.length > 0
      : false,
    shouldShowUnderConstruction,
    customStyle,
    shouldShowNumberOnMenu: isTrueOrDefault(shouldShowNumberOnMenu, false),
    providerPhone,
    headerText: headerText || defaultConfig.headerText,
    footerText: footerText || defaultConfig.footerText,
    formatPhone: isTrueOrDefault(formatPhone, defaultConfig.formatPhone),
    eligibilityZipCodes,
    appLogo,
    heroImage,
    withCentivo: withCentivo ?? [],
    networkOfExcellence:
      networkOfExcellence && networkOfExcellence.length !== 0
        ? networkOfExcellence
        : [
            'The Centivo Network is built around trusted, quality providers proven to deliver top results for their patients.',
            'The Centivo Network includes all the doctors and specialists you may need, including virtual options.',
          ],
    heroBtn,
    externalLink,
    description,
    video: {
      enabled: videoEnabled,
      url: videoUrl,
    },
    pageTitle,
    preferredNetworkCodes,
  }
}
